import { Pencil } from 'lucide-react'
import { useWorkerLoginStatus, type WorkerLoginState } from '../../accounts/index.js'
import { Button, DetailFields, DetailSection, LoadFailure, Skeleton, TIER_LABELS, WORKER_ROLE_LABELS, credentialsText, formatDate } from '../../core/index.js'
import { RevealableAddress, useWorkerContacts, type ContactView } from '../../messages/index.js'
import { useClinicalBridge } from '../../schedule/index.js'
import { useWorkerProfile } from '../hooks.js'
import { EMPLOYMENT_TYPE_LABELS, type WorkerProfile } from '../types.js'

const STATE_CHIP: Record<WorkerLoginState, string> = {
  none: 'bg-marine-lt text-ink-mid',
  invited: 'bg-gold/20 text-ink',
  expired: 'bg-coral/15 text-ink',
  accepted: 'bg-green/15 text-ink',
  password: 'bg-marine-lt text-ink',
}

interface WorkerRecordPanelProps {
  workerId: string
  facilityId: string
  /** Undefined until the session names the client; the login then waits. */
  clientId: string | undefined
  /** Opens the Edit worker dialog; null where the reader may not edit. */
  onEdit: (() => void) | null
}

function notRecorded(value: string | number | null | undefined, unit = ''): string {
  return value === null || value === undefined || value === '' ? 'Not recorded' : `${value}${unit}`
}

/** The mobile's state as the Edit worker dialog shows it: whose it is and whether it can be texted. */
function mobileState(sms: ContactView): string {
  if (sms.awaitingAgreement) {
    return 'waiting for the worker to agree'
  }
  if (sms.optedOut) {
    return 'the worker replied STOP'
  }
  return sms.verifiedAt ? 'verified' : 'not verified yet'
}

/** The number masked, with the eye that shows it whole, then its state. */
function MobileValue({ workerId, sms }: { workerId: string; sms: ContactView | null }) {
  if (!sms) {
    return <>None on file</>
  }
  return (
    <>
      <RevealableAddress workerId={workerId} contact={sms} /> · {mobileState(sms)}
    </>
  )
}

/** The Bridge switch in words: null while it loads, undefined when it could not be read. */
function bridgeText(eligible: boolean | null | undefined): string {
  if (eligible === undefined) {
    return 'Could not be read'
  }
  return eligible === null ? '…' : eligible ? 'Eligible' : 'Not eligible'
}

function recordFields(profile: WorkerProfile, bridgeEligible: boolean | null | undefined, sms: ContactView | null, contactsLoaded: boolean) {
  const units = profile.units ?? null
  return [
    { label: 'Name', value: profile.name },
    { label: 'Role', value: `${profile.role} · ${WORKER_ROLE_LABELS[profile.role]}` },
    { label: 'Employment', value: EMPLOYMENT_TYPE_LABELS[profile.employmentType ?? 'full_time'] },
    { label: 'Hire date', value: profile.hireDate ? formatDate(profile.hireDate) : 'Not recorded' },
    { label: 'Leave date', value: profile.leaveDate ? formatDate(profile.leaveDate) : 'Still here' },
    { label: 'Call-outs this cycle', value: notRecorded(profile.callOuts) },
    { label: 'Charting %', value: notRecorded(profile.chartingPct, '%') },
    { label: 'PTO balance', value: notRecorded(profile.ptoBalanceDays, ' days') },
    { label: 'Contracted hours', value: notRecorded(profile.contractedHoursPerWeek, ' a week') },
    { label: 'Home unit', value: units?.primary ?? 'Any unit' },
    { label: 'Floats to', value: units && units.secondary.length > 0 ? units.secondary.join(', ') : 'None' },
    { label: 'Credentials', value: credentialsText(profile.credentials) },
    { label: 'Clinical Bridge', value: bridgeText(bridgeEligible) },
    { label: 'Mobile', value: contactsLoaded ? <MobileValue workerId={profile.workerId} sms={sms} /> : '…' },
    { label: 'Tier held', value: TIER_LABELS[profile.tier] },
    { label: 'Score', value: String(profile.score) },
    ...(profile.employeeId ? [{ label: 'Employee ID', value: profile.employeeId, mono: true }] : []),
  ]
}

/**
 * Everything the Edit worker dialog holds, read-only, in the dialog's own
 * order, and the worker's login: whether they were invited, whether the
 * invitation waits, lapsed or was accepted and when, and when they last
 * signed in. The first tab of an open roster row, so expanding a worker
 * shows the same record the dialog edits (the user's request, 29 October
 * 2026).
 */
export function WorkerRecordPanel({ workerId, facilityId, clientId, onEdit }: WorkerRecordPanelProps) {
  const profileQuery = useWorkerProfile(workerId)
  const bridgeQuery = useClinicalBridge(workerId)
  const contactsQuery = useWorkerContacts(workerId)
  const login = useWorkerLoginStatus(clientId, facilityId, workerId)
  const sms = contactsQuery.data?.channels.find((contact) => contact.channel === 'sms') ?? null

  return (
    <div className="border-t border-marine/15 bg-canvas px-5 pb-5 pt-4">
      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2">
          <DetailSection title="Record">
            {profileQuery.isPending ? (
              <Skeleton lines={6} />
            ) : profileQuery.isError ? (
              <LoadFailure size="xs" message="The worker’s record could not be loaded." onRetry={() => void profileQuery.refetch()} retrying={profileQuery.isFetching} />
            ) : (
              <DetailFields fields={recordFields(profileQuery.data, bridgeQuery.isError ? undefined : (bridgeQuery.data?.eligible ?? null), sms, !contactsQuery.isPending)} />
            )}
          </DetailSection>
        </div>
        <div className="space-y-4">
          <DetailSection title="App login">
            {login.isPending ? (
              <Skeleton lines={3} />
            ) : login.isError || !login.status ? (
              <LoadFailure size="xs" message="The login could not be loaded." onRetry={login.refetch} retrying={login.isFetching} />
            ) : (
              <div className="space-y-2 text-xs text-ink">
                <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${STATE_CHIP[login.status.state]}`}>{login.status.label}</span>
                {login.login && <div className="break-words font-mono">{login.login.email}</div>}
                <div className="text-ink-mid">{login.status.detail}</div>
                {login.login && login.status.state !== 'invited' && login.status.state !== 'expired' && (
                  <div className="text-ink-mid">Last signed in {login.login.lastSignedInAt ? formatDate(login.login.lastSignedInAt) : 'never'}</div>
                )}
              </div>
            )}
          </DetailSection>
          {onEdit && (
            <Button icon={Pencil} variant="secondary" size="sm" onClick={onEdit}>
              Edit worker
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
